<template>
  <ReportWrapper
    :loading="loading"
    :on-load="loadReport"
    :tab-lv1-index="tabLv1Index"
    :tab-lv2-index="tabLv2Index"
    :useMonthPicker="true"
    :showProviderFilter="true"
  >
    <ReportTable minWidth="1560px">
      <thead>
        <tr>
          <th><span>Name</span></th>
          <th>
            <TableHeaderWithTooltip
              title="Completed exams"
              :tooltip="tooltips['Completed exams']"
            />
          </th>
          <th>
            <TableHeaderWithTooltip
              title="Originating exams"
              :tooltip="tooltips['Originating exams']"
            />
          </th>
          <th>
            <TableHeaderWithTooltip
              title="Diagnosed"
              :tooltip="tooltips['Diagnosed']"
            />
          </th>
          <th>
            <TableHeaderWithTooltip
              title="Accepted"
              :tooltip="tooltips['Accepted']"
            />
          </th>
          <th><span>Diagnosed / exam</span></th>
          <th><span>Diagnosed / patient</span></th>
          <th><span>Accepted / exam</span></th>
          <th><span>Accepted / patient</span></th>
          <th>
            <TableHeaderWithTooltip
              title="Acceptance %"
              :tooltip="tooltips['Acceptance %']"
            />
          </th>
          <th><span>Acceptance % patient</span></th>
        </tr>
      </thead>
      <tbody v-if="!loading">
        <tr
          v-for="(item, index) in reportData"
          :key="index"
          :class="{
            active: item.isCurrentUserRecord,
            median: item.isMedian,
            hidden: !item.isActive || item.isHidden || item.isHiddenReport,
          }"
        >
          <td>{{ item.fullName }}</td>
          <td>
            <p>{{ item.examsCount }}</p>
            <p
              v-if="item.highestExamsCount"
              class="text-xsmall"
            >
              Highest: {{ item.highestExamsCount.value }} ({{ item.highestExamsCount.month }})
            </p>
            <p
              v-if="item.highestExamsCount && item.lowestExamsCount"
              class="text-xsmall"
            >
              Average: {{ (item.highestExamsCount.value + item.lowestExamsCount.value) / 2 }}
            </p>
            <p
              v-if="item.idealExamsCount?.value"
              class="text-xsmall font-bold"
            >
              Ideal: {{ item.idealExamsCount.value }}
            </p>
          </td>
          <td>{{ item.originatingExamsCount }}</td>
          <td>{{ formatMoney(item.diagnosedProcedureAmount) }}</td>
          <td>
            <p>{{ formatMoney(item.acceptedProcedureAmount) }}</p>
            <p
              v-if="item.highestAcceptedProcedureAmount"
              class="text-xsmall"
            >
              Highest: {{ formatMoney(item.highestAcceptedProcedureAmount.value) }} ({{
                item.highestAcceptedProcedureAmount.month
              }})
            </p>
          </td>
          <td>{{ formatMoney(item.diagnosedPerExam) }}</td>
          <td>{{ formatMoney(item.diagnosedPerPatient) }}</td>
          <td>
            <p>{{ formatMoney(item.acceptedPerExam) }}</p>
            <p
              v-if="item.highestAcceptedPerExam"
              class="text-xsmall"
            >
              Highest: {{ formatMoney(item.highestAcceptedPerExam.value) }} ({{ item.highestAcceptedPerExam.month }})
            </p>
          </td>
          <td>
            <p>{{ formatMoney(item.acceptedPerPatient) }}</p>
            <p
              v-if="item.highestAcceptedPerPatient"
              class="text-xsmall"
            >
              Highest: {{ formatMoney(item.highestAcceptedPerPatient.value) }} ({{
                item.highestAcceptedPerPatient.month
              }})
            </p>
          </td>
          <td>
            <p>{{ formatPercentage(item.eievAcceptanceRate ?? 0) }}</p>
            <p
              v-if="item.highestAcceptanceRate"
              class="text-xsmall"
            >
              Highest: {{ formatPercentage(item.highestAcceptanceRate.value) }} ({{
                item.highestAcceptanceRate.month
              }})
            </p>
          </td>
          <td>
            <p>{{ formatPercentage(item.eievAcceptanceRatePatientBased ?? 0) }}</p>
            <p
              v-if="item.highestAcceptanceRate"
              class="text-xsmall"
            >
              Highest: {{ formatPercentage(item.highestAcceptanceRatePatientBased.value) }} ({{
                item.highestAcceptanceRatePatientBased.month
              }})
            </p>
          </td>
        </tr>

        <NoRecordsFound
          v-if="!reportData.length && !loading"
          colspan="9"
        />
      </tbody>
    </ReportTable>

    <MetricDefinitionList
      v-if="!loading"
      :metrics="tooltips"
    />
  </ReportWrapper>
</template>

<script>
import DataService from '../../../../_services/DataService';
import MetricDefinitionList from '../../../components/Reporting/MetricDefinitionList';
import NoRecordsFound from '../../../components/Reporting/NoRecordsFound';
import ReportTable from '../../../components/Reporting/ReportTable.vue';
import ReportWrapper from '../../../components/Reporting/ReportWrapper';
import TableHeaderWithTooltip from '../../../components/Reporting/TableHeaderWithTooltip';
import Utils from '../../../utils';

export default {
  name: 'ClinicEIEV',
  components: {
    MetricDefinitionList,
    NoRecordsFound,
    ReportTable,
    ReportWrapper,
    TableHeaderWithTooltip,
  },
  inject: ['shouldShowHiddenProviders'],
  props: ['tabLv1Index', 'tabLv2Index'],
  data() {
    return {
      reportDataOriginal: [],
      loading: false,
      tooltips: {
        'Completed exams': 'Completed exam prodedures within the selected time period',
        'Originating exams': 'Exam prodedures leading to the diagnosed amount, may fall outside the selected time period',
        Diagnosed: 'Dollar amount of diagnosed procedures',
        Accepted: 'Dollar amount of scheduled & completed procedures',
        'Acceptance %': 'Accepted / Diagnosed',
        'Ideal exam volume': 'The estimated ideal number of exams is the monthly total of exams which corresponds to the highest dollar volume of accepted treatment',
      },
    };
  },
  computed: {
    showHiddenProviders() {
      return this.shouldShowHiddenProviders();
    },
    reportData() {
      return this.showHiddenProviders
        ? this.reportDataOriginal
        : this.reportDataOriginal.filter(this.providerIsVisible);
    },
  },
  methods: {
    async loadReport(payload) {
      const { month } = payload;
      this.loading = true;

      const result = await DataService.getClinicEIEVReport({ month });
      this.reportDataOriginal = result.status === 200 ? result.data.dataPerProvider ?? [] : [];

      this.loading = false;
    },
    formatMoney(number) {
      return Utils.formatCurrency(number, false, false);
    },
    formatPercentage(number) {
      return Utils.formatPercentage(number);
    },
    providerIsVisible(provider) {
      return provider.isActive && !provider.isHidden && !provider.isHiddenReport;
    },
  },
};
</script>

<style scoped>
p {
  margin: 0;
}

table > tbody > tr > td {
  height: 118px !important;
}

.text-xsmall {
  font-size: 12px !important;
}

.font-bold {
  font-weight: bold;
}
</style>
